import React, { useState, useRef, useEffect } from 'react';
import { RiseOutlined, DollarOutlined, UserOutlined, DragOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';

const MetricsSidebar = styled.div<{ $width: number; $height: number; $x: number; $y: number }>`
  position: fixed;
  top: ${props => props.$y}px;
  left: ${props => props.$x}px;
  width: ${props => props.$width}px;
  height: ${props => props.$height}px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  padding: 16px;
  z-index: 1000;
  overflow-y: auto;
  border: 1px solid #e0e0e0;
  min-width: 280px;
  min-height: 300px;
  max-width: 500px;
  max-height: 80vh;
`;

const DragHandle = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 40px;
  cursor: move;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 12px 12px 0 0;
  border-bottom: 1px solid #e0e0e0;
  
  &:hover {
    background: linear-gradient(135deg, #e9ecef 0%, #dee2e6 100%);
  }
`;

const DragIcon = styled.div`
  color: #6c757d;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ResizeHandle = styled.div`
  position: absolute;
  bottom: 0;
  right: 0;
  width: 20px;
  height: 20px;
  cursor: nw-resize;
  background: linear-gradient(135deg, transparent 0%, transparent 40%, #6c757d 40%, #6c757d 60%, transparent 60%);
  border-radius: 0 0 12px 0;
  
  &:hover {
    background: linear-gradient(135deg, transparent 0%, transparent 40%, #495057 40%, #495057 60%, transparent 60%);
  }
`;

const ContentWrapper = styled.div`
  margin-top: 40px;
  height: calc(100% - 40px);
  overflow-y: auto;
`;

const LocationHeader = styled.div`
  margin-bottom: 16px;
  padding-left: 12px;
  border-left: 3px solid #4285f4;
`;

const LocationTitle = styled.h2`
  margin: 0 0 3px 0;
  color: #202124;
  font-size: 16px;
  font-weight: 600;
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 16px;
`;

const MetricCard = styled.div`
  text-align: center;
  padding: 12px 8px;
  background: #f8f9fa;
  border-radius: 6px;
`;

const MetricValue = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: #202124;
  margin-bottom: 3px;
`;

const MetricLabel = styled.div`
  font-size: 10px;
  color: #5f6368;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  font-weight: 500;
`;

const InsightsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;
`;

const InsightCard = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border-radius: 6px;
  
  &.positive {
    background: #e8f5e8;
  }
  
  &.warning {
    background: #fff8e1;
  }
  
  &.info {
    background: #e3f2fd;
  }
`;

const InsightIcon = styled.div`
  margin-right: 8px;
  font-size: 14px;
`;

const InsightValue = styled.span`
  font-weight: 700;
  font-size: 14px;
  margin-right: 6px;
`;

const InsightLabel = styled.span`
  font-size: 12px;
  color: #5f6368;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  background: none;
  border: none;
  font-size: 16px;
  color: #5f6368;
  cursor: pointer;
  padding: 2px;
  border-radius: 3px;
  
  &:hover {
    background: #f1f3f4;
    color: #202124;
  }
`;

const SkeletonCard = styled.div`
  text-align: center;
  padding: 12px 8px;
  background: #f8f9fa;
  border-radius: 6px;
  
  .skeleton-value {
    height: 24px;
    background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
    background-size: 200% 100%;
    animation: loading 1.5s infinite;
    border-radius: 4px;
    margin-bottom: 3px;
  }
  
  .skeleton-label {
    height: 12px;
    background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
    background-size: 200% 100%;
    animation: loading 1.5s infinite;
    border-radius: 2px;
    width: 80%;
    margin: 0 auto;
  }
  
  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const SkeletonInsight = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border-radius: 6px;
  background: #f8f9fa;
  
  .skeleton-icon {
    width: 14px;
    height: 14px;
    background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
    background-size: 200% 100%;
    animation: loading 1.5s infinite;
    border-radius: 50%;
    margin-right: 8px;
  }
  
  .skeleton-content {
    flex: 1;
    height: 16px;
    background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
    background-size: 200% 100%;
    animation: loading 1.5s infinite;
    border-radius: 2px;
  }
  
  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

interface PrimaryMetricModalProps {
  visible: boolean;
  onClose: () => void;
  locationName: string;
  metrics: {
    clients: number;
    prospects: number;
    vendors: number;
    conversionRate: number;
    revenue: number;
    yoyGrowth: number;
    avgDeal: number;
    custProspectRatio: number;
  };
  isLoading?: boolean;
}

const PrimaryMetricModal: React.FC<PrimaryMetricModalProps> = ({
  visible,
  onClose,
  locationName,
  metrics,
  isLoading = false
}) => {
  // State for position and size
  const [position, setPosition] = useState({ x: window.innerWidth - 340, y: 80 });
  const [size, setSize] = useState({ width: 320, height: 400 });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0 });

  const sidebarRef = useRef<HTMLDivElement>(null);

  // Handle drag start
  const handleDragStart = (e: React.MouseEvent) => {
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  };

  // Handle resize start
  const handleResizeStart = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsResizing(true);
    setResizeStart({
      x: e.clientX,
      y: e.clientY,
      width: size.width,
      height: size.height
    });
  };

  // Handle mouse move
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        const newX = Math.max(0, Math.min(window.innerWidth - size.width, e.clientX - dragStart.x));
        const newY = Math.max(0, Math.min(window.innerHeight - size.height, e.clientY - dragStart.y));
        setPosition({ x: newX, y: newY });
      }

      if (isResizing) {
        const deltaX = e.clientX - resizeStart.x;
        const deltaY = e.clientY - resizeStart.y;
        const newWidth = Math.max(280, Math.min(500, resizeStart.width + deltaX));
        const newHeight = Math.max(300, Math.min(window.innerHeight * 0.8, resizeStart.height + deltaY));
        setSize({ width: newWidth, height: newHeight });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setIsResizing(false);
    };

    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = '';
    };
  }, [isDragging, isResizing, dragStart, resizeStart, size]);

  const formatNumber = (num: number): string => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  const formatCurrency = (num: number): string => {
    if (num >= 1000000) {
      return '$' + (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return '$' + (num / 1000).toFixed(1) + 'K';
    }
    return '$' + num.toLocaleString();
  };

  const formatPercentage = (num: number): string => {
    return num.toFixed(1) + '%';
  };

  if (!visible) return null;

  return (
    <MetricsSidebar
      ref={sidebarRef}
      $x={position.x}
      $y={position.y}
      $width={size.width}
      $height={size.height}
    >
      <DragHandle onMouseDown={handleDragStart}>
        <DragIcon>
          <DragOutlined />
          <span style={{ fontSize: '12px', marginLeft: '4px' }}>Primary Metrics</span>
        </DragIcon>
      </DragHandle>

      <CloseButton onClick={onClose}>×</CloseButton>
      
      <ContentWrapper>
        <LocationHeader>
          <LocationTitle>{locationName}</LocationTitle>
          {/* <PerformanceBadge>HIGH PERFORMER</PerformanceBadge> */}
        </LocationHeader>

        {isLoading ? (
          <>
            <MetricsGrid>
              <SkeletonCard>
                <div className="skeleton-value"></div>
                <div className="skeleton-label"></div>
              </SkeletonCard>
              <SkeletonCard>
                <div className="skeleton-value"></div>
                <div className="skeleton-label"></div>
              </SkeletonCard>
              <SkeletonCard>
                <div className="skeleton-value"></div>
                <div className="skeleton-label"></div>
              </SkeletonCard>
              <SkeletonCard>
                <div className="skeleton-value"></div>
                <div className="skeleton-label"></div>
              </SkeletonCard>
            </MetricsGrid>

            <InsightsGrid>
              <SkeletonInsight>
                <div className="skeleton-icon"></div>
                <div className="skeleton-content"></div>
              </SkeletonInsight>
              <SkeletonInsight>
                <div className="skeleton-icon"></div>
                <div className="skeleton-content"></div>
              </SkeletonInsight>
              <SkeletonInsight>
                <div className="skeleton-icon"></div>
                <div className="skeleton-content"></div>
              </SkeletonInsight>
            </InsightsGrid>
          </>
        ) : (
          <>
            <MetricsGrid>
              <MetricCard>
                <MetricValue>{formatNumber(metrics.clients)}</MetricValue>
                <MetricLabel>CUSTOMERS</MetricLabel>
              </MetricCard>
              <MetricCard>
                <MetricValue>{formatNumber(metrics.prospects)}</MetricValue>
                <MetricLabel>PROSPECTS</MetricLabel>
              </MetricCard>
              <MetricCard>
                <MetricValue>{formatPercentage(metrics.conversionRate)}</MetricValue>
                <MetricLabel>CONVERSION RATE</MetricLabel>
              </MetricCard>
              <MetricCard>
                <MetricValue>{formatCurrency(metrics.revenue)}</MetricValue>
                <MetricLabel>REVENUE</MetricLabel>
              </MetricCard>
            </MetricsGrid>

            <InsightsGrid>
              <InsightCard className="positive">
                <InsightIcon>
                  <RiseOutlined style={{ color: '#34a853' }} />
                </InsightIcon>
                <InsightValue style={{ color: '#34a853' }}>
                  +{formatPercentage(metrics.yoyGrowth)}
                </InsightValue>
                <InsightLabel>YoY Growth</InsightLabel>
              </InsightCard>
              
              <InsightCard className="warning">
                <InsightIcon>
                  <DollarOutlined style={{ color: '#fbbc04' }} />
                </InsightIcon>
                <InsightValue style={{ color: '#ea8600' }}>
                  {formatCurrency(metrics.avgDeal)}
                </InsightValue>
                <InsightLabel>Avg Deal</InsightLabel>
              </InsightCard>
              
              <InsightCard className="info">
                <InsightIcon>
                  <UserOutlined style={{ color: '#4285f4' }} />
                </InsightIcon>
                <InsightValue style={{ color: '#1a73e8' }}>
                  {metrics.custProspectRatio.toFixed(2)}
                </InsightValue>
                <InsightLabel>Cust/Pros Ratio</InsightLabel>
              </InsightCard>
            </InsightsGrid>
          </>
        )}
      </ContentWrapper>

      <ResizeHandle onMouseDown={handleResizeStart} />
    </MetricsSidebar>
  );
};

export default PrimaryMetricModal;
